import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Database, TrendingUp, Download } from 'lucide-react';

const HomePage: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    return (
        <div className="bg-white">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-primary-600">
                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-32">
                    <div className="text-center">
                        <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                            <span className="block">Search the Global</span>
                            <span className="block">LEI Database</span>
                        </h1>
                        <p className="mt-6 text-xl text-blue-100 max-w-3xl mx-auto">
                            Explore Legal Entity Identifiers (LEIs) from around the world. Search by
                            company name, country, legal form, and more.
                        </p>

                        {/* Search Form */}
                        <div className="mt-8 max-w-xl mx-auto">
                            <form onSubmit={handleSearch} className="flex gap-3">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                    <input
                                        type="text"
                                        placeholder="Search by LEI, entity name, or other identifiers..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="bg-white text-primary-600 px-8 py-3 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Search
                                </button>
                            </form>
                            <p className="mt-4 text-blue-100 text-sm">
                                Or{' '}
                                <Link
                                    to="/search"
                                    className="text-white underline hover:no-underline"
                                >
                                    browse with advanced filters
                                </Link>
                            </p>
                        </div>
                    </div>
                </main>
            </div>

            {/* Features Section */}
            <div className="py-12 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:text-center">
                        <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">
                            Features
                        </h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                            Comprehensive LEI Search & Analysis
                        </p>
                        <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                            Powerful search capabilities with advanced filtering and data export
                            options.
                        </p>
                    </div>

                    <div className="mt-10">
                        <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    <Search className="h-6 w-6" />
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Advanced Search
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Full-text search across company names, addresses, and legal
                                    forms with fuzzy matching and faceted filtering.
                                </p>
                            </div>

                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    <Database className="h-6 w-6" />
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Global Coverage
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Access LEI data from all jurisdictions worldwide, including
                                    comprehensive entity information and status updates.
                                </p>
                            </div>

                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    <Download className="h-6 w-6" />
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Data Export
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Export search results in CSV or JSON format for further analysis
                                    and integration with other systems.
                                </p>
                            </div>

                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    <TrendingUp className="h-6 w-6" />
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Real-time Updates
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Daily updates from GLEIF ensure you have the most current and
                                    accurate LEI information available.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="bg-primary-600">
                <div className="max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
                    <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
                        <div>
                            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                                Trusted by Financial Professionals Worldwide
                            </h2>
                            <p className="mt-3 max-w-3xl text-lg text-primary-100">
                                The LEI system is the global standard for identifying legal entities
                                in financial transactions. Our platform provides easy access to this
                                critical information.
                            </p>
                            <div className="mt-8 sm:flex">
                                <div className="rounded-md shadow-sm">
                                    <Link
                                        to="/search"
                                        className="flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50"
                                    >
                                        Explore Database
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div className="mt-8 grid grid-cols-2 gap-0.5 md:grid-cols-2 lg:mt-0">
                            <div className="col-span-1 flex justify-center py-8 px-8 bg-primary-500">
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-white">3M+</div>
                                    <div className="text-primary-100">LEI Records</div>
                                </div>
                            </div>
                            <div className="col-span-1 flex justify-center py-8 px-8 bg-primary-500">
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-white">200+</div>
                                    <div className="text-primary-100">Countries</div>
                                </div>
                            </div>
                            <div className="col-span-1 flex justify-center py-8 px-8 bg-primary-500">
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-white">500+</div>
                                    <div className="text-primary-100">LOUs</div>
                                </div>
                            </div>
                            <div className="col-span-1 flex justify-center py-8 px-8 bg-primary-500">
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-white">Daily</div>
                                    <div className="text-primary-100">Updates</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
